import React, { useState } from "react";
import {
    odometerApi,
    OdometerApiError,
    OdometerCycle,
    OdometerVehicleStatus,
} from "../../data/odometerApi";

interface OdometerScheduleModalProps {
    vehicle: OdometerVehicleStatus;
    onClose: () => void;
    onSaved: (vehicles: OdometerVehicleStatus[]) => void;
}

interface CycleOption {
    value: OdometerCycle;
    label: string;
    description: string;
}

const CYCLE_OPTIONS: CycleOption[] = [
    { value: "NONE", label: "미설정", description: "리마인드 알림을 만들지 않습니다." },
    { value: "WEEKLY", label: "주간", description: "기산일부터 7일마다 입력을 요청합니다." },
    { value: "BIWEEKLY", label: "격주", description: "기산일부터 14일마다 입력을 요청합니다." },
    { value: "MONTHLY", label: "월간", description: "기산일부터 매월 같은 날 입력을 요청합니다." },
];

const todayString = (): string => {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export default function OdometerScheduleModal({
    vehicle,
    onClose,
    onSaved,
}: OdometerScheduleModalProps) {
    const [cycle, setCycle] = useState<OdometerCycle>(vehicle.cycle);
    const [anchorDate, setAnchorDate] = useState<string>(vehicle.anchorDate || todayString());
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const selectedOption = CYCLE_OPTIONS.find((option) => option.value === cycle) || CYCLE_OPTIONS[0];

    const handleSave = async () => {
        setIsSaving(true);
        setErrorMessage(null);
        try {
            const response = await odometerApi.upsertSchedule(vehicle.vin, cycle, anchorDate);
            onSaved(response.vehicles);
        } catch (error) {
            setErrorMessage(
                error instanceof OdometerApiError ? error.message : "주기 설정 중 오류가 발생했습니다."
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="odo-modal-backdrop" onClick={onClose}>
            <div className="odo-modal narrow" onClick={(event) => event.stopPropagation()}>
                <div className="odo-modal-header">
                    <div>
                        <h2 className="odo-modal-title">입력 주기 설정</h2>
                        <div className="odo-modal-subtitle">
                            {vehicle.vehicleNumber}
                            {vehicle.model ? ` · ${vehicle.model}` : ""}
                        </div>
                    </div>
                    <button type="button" className="odo-close-btn" onClick={onClose} aria-label="닫기">
                        &times;
                    </button>
                </div>

                <div className="odo-modal-body">
                    <section className="odo-section">
                        <div className="odo-section-title">주기</div>
                        <div className="odo-cycle-options">
                            {CYCLE_OPTIONS.map((option) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    className={`odo-cycle-option ${cycle === option.value ? "selected" : ""}`}
                                    onClick={() => setCycle(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <div className="odo-hint">{selectedOption.description}</div>
                    </section>

                    {cycle !== "NONE" && (
                        <section className="odo-section">
                            <div className="odo-field">
                                <label htmlFor="odo-anchor-date">기산일</label>
                                <input
                                    id="odo-anchor-date"
                                    type="date"
                                    value={anchorDate}
                                    onChange={(event) => setAnchorDate(event.target.value)}
                                />
                            </div>
                            <div className="odo-hint">
                                기산일부터 주기를 반복해 다음 입력 예정일을 계산합니다. 예정일이 되면 리마인드
                                알림이 생성됩니다.
                            </div>
                        </section>
                    )}

                    {vehicle.nextDueDate && (
                        <div className="odo-banner info">
                            현재 다음 입력 예정일: <strong>{vehicle.nextDueDate}</strong>
                        </div>
                    )}
                    {errorMessage && <div className="odo-banner error">{errorMessage}</div>}
                </div>

                <div className="odo-modal-footer">
                    <button type="button" className="odo-btn ghost" onClick={onClose} disabled={isSaving}>
                        취소
                    </button>
                    <button type="button" className="odo-btn primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? "저장 중..." : "저장"}
                    </button>
                </div>
            </div>
        </div>
    );
}
